import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Trash2, Check, Clock, Plus, X, Send, RotateCcw, Copy as CopyIcon,
  CheckCircle2, AlertCircle, Calendar, RefreshCw, HardDrive, ExternalLink, UploadCloud,
  ChevronLeft, ChevronRight, Eye, Users, Film, Clapperboard,
} from 'lucide-react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useIsMobile } from '../hooks/useResponsive';
import MoreMenu from '../components/MoreMenu';
import InstagramPreview from '../components/InstagramPreview';
import { PubPill, FormatTag, isVideo, SegnaliContenuto } from '../components/content';
import { can, RUOLI_SOCIAL } from '../lib/ruoli';
import ProduzionePanel from '../components/ProduzionePanel';
import VersioniReview from '../components/VersioniReview';
import MediaCheck from '../components/MediaCheck';
import CaptionEditor from '../components/CaptionEditor';
import ProssimoPasso, { calcolaPasso } from '../components/ProssimoPasso';

const STATI = ['Idea', 'In Scrittura', 'In Design', 'In Review', 'Approvato', 'Pubblicato', 'Scartato'];
const FORMATI = ['Reel', 'Carosello', 'Storia', 'Post Singolo', 'Articolo SEO', 'Video YT', 'Newsletter'];
const PIATTAFORME = ['Instagram', 'Facebook', 'TikTok', 'YouTube', 'LinkedIn', 'Twitter'];

const STATO_STYLE = {
  'Idea': { bg: 'var(--bg3)', color: 'var(--text-muted)' },
  'In Scrittura': { bg: 'var(--yellow-dim)', color: 'var(--yellow)' },
  'In Design': { bg: 'var(--purple-dim)', color: 'var(--purple)' },
  'In Review': { bg: 'rgba(59,130,246,0.12)', color: '#60a5fa' },
  'Approvato': { bg: 'var(--green-dim)', color: 'var(--green)' },
  'Pubblicato': { bg: 'var(--green-dim)', color: 'var(--green)' },
  'Scartato': { bg: 'var(--red-dim)', color: 'var(--red)' },
};

function orizzonteDaData(dp) {
  if (!dp) return 'pre-ped';
  const d = new Date(dp); const now = new Date();
  const dm = d.getFullYear() * 12 + d.getMonth();
  const nm = now.getFullYear() * 12 + now.getMonth();
  if (dm === nm) return 'corrente';
  if (dm === nm + 1) return 'mese_prossimo';
  if (dm >= nm + 2) return 'mese_dopo';
  return 'pre-ped';
}
const ORIZZONTI_LABEL = { 'pre-ped': 'PRE-PED · Bozza', corrente: 'Mese corrente', mese_prossimo: 'Mese prossimo', mese_dopo: 'Mese dopo' };

// Campi del form (gli incaricati e la fase si gestiscono dal pannello Produzione)
const EMPTY = {
  id_post: '', titolo_post: '', hook: '', formato: 'Post Singolo', piattaforma: 'Instagram',
  stato: 'Idea', agente_copy: '', agente_visual: '', data_pubblicazione: '', ora_pubblicazione: '', modalita_storia: 'Auto', orizzonte: 'corrente',
  ads_campagna: '', ads_stato: '', spending_speso: '', spending_budget: '',
  copy_ok: false, visual_ok: false, link_asset: '', link_pubblicato: '',
  media_url: [], caption_pubblica: '',
};

function formDaRiga(d) {
  return {
    id_post: d.id_post || '', titolo_post: d.titolo_post || '', hook: d.hook || '',
    formato: d.formato || 'Post Singolo', piattaforma: d.piattaforma || 'Instagram',
    stato: d.stato || 'Idea', agente_copy: d.agente_copy || '', agente_visual: d.agente_visual || '',
    data_pubblicazione: d.data_pubblicazione || '', ora_pubblicazione: (d.ora_pubblicazione || '').slice(0, 5),
    modalita_storia: d.modalita_storia || 'Auto', orizzonte: d.orizzonte || 'corrente',
    ads_campagna: d.ads_campagna || '', ads_stato: d.ads_stato || '',
    spending_speso: d.spending_speso ?? '', spending_budget: d.spending_budget ?? '',
    copy_ok: !!d.copy_ok, visual_ok: !!d.visual_ok,
    link_asset: d.link_asset || '', link_pubblicato: d.link_pubblicato || '',
    media_url: Array.isArray(d.media_url) ? d.media_url : [], caption_pubblica: d.caption_pubblica || '',
  };
}

// Valori da inviare al server per un campo del form
function valoreServer(k, v) {
  if (k === 'spending_speso' || k === 'spending_budget') return parseFloat(v) || 0;
  if (['data_pubblicazione', 'ora_pubblicazione'].includes(k)) return v || null;
  return v;
}

/* ── piccoli componenti di layout ───────────────────────────────────────── */
function Field({ label, hint, children, full }) {
  return (
    <label style={{ display: 'block', gridColumn: full ? '1 / -1' : 'auto', minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600 }}>{label}</span>
      {children}
      {hint && <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-dim)', marginTop: 5 }}>{hint}</span>}
    </label>
  );
}

function Section({ title, icon: Icon, right, children, id }) {
  return (
    <section id={id} className="eden-card" style={{ padding: '18px 20px', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        {Icon && <Icon size={16} color="var(--text-dim)" />}
        <h2 style={{ fontSize: 14, fontWeight: 650, color: 'var(--text)', margin: 0 }}>{title}</h2>
        {right && <div style={{ marginLeft: 'auto' }}>{right}</div>}
      </div>
      {children}
    </section>
  );
}

function Collapsible({ title, children }) {
  return (
    <details className="eden-card" style={{ padding: '14px 20px', marginBottom: 14 }}>
      <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 650, color: 'var(--text)', listStyle: 'revert' }}>{title}</summary>
      <div style={{ marginTop: 14 }}>{children}</div>
    </details>
  );
}

export default function PedContenuto() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const { success, error } = useToast();
  const isMobile = useIsMobile();
  const isNew = id === 'nuovo';
  // Approvare, programmare ed eliminare: admin/boss/social media manager (lib/ruoli.js)
  const puoPubblicare = can(user, RUOLI_SOCIAL);
  const puoApprovare = puoPubblicare || user?.ruolo === 'direttore';

  const [form, setForm] = useState(EMPTY);
  const [orig, setOrig] = useState(EMPTY);          // ultimo stato salvato (per sapere cosa è cambiato)
  const [meta, setMeta] = useState({});             // campi calcolati: cliente, pubblicazione, approvazioni…
  const [clienteId, setClienteId] = useState(sp.get('cliente_id') || '');
  const [clientiLista, setClientiLista] = useState([]);
  const scegliCliente = isNew && !sp.get('cliente_id');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [upload, setUpload] = useState(null);       // { nome, pct } durante il caricamento
  const [dragOver, setDragOver] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [storico, setStorico] = useState([]);
  const fileRef = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const loadStorico = useCallback(() => {
    if (!isNew) api.get(`/ped/riga/${id}/storico`, { silent: true }).then(r => setStorico(r.data || [])).catch(() => {});
  }, [id, isNew]);

  useEffect(() => {
    if (scegliCliente) api.get('/clienti').then(r => setClientiLista(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [scegliCliente]);

  useEffect(() => {
    if (isNew) {
      const d = sp.get('data');
      const f = { ...EMPTY, data_pubblicazione: d || '', orizzonte: orizzonteDaData(d) };
      setForm(f); setOrig(f); setMeta({}); setLoading(false);
      return;
    }
    setLoading(true);
    api.get(`/ped/riga/${id}`).then(r => {
      const f = formDaRiga(r.data);
      setForm(f); setOrig(f);
      setClienteId(r.data.cliente_id);
      setMeta(r.data);
      setLoading(false);
    }).catch(() => { error('Contenuto non trovato'); navigate(-1); });
    loadStorico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Campi modificati rispetto all'ultimo salvataggio
  const modificati = useMemo(() => Object.keys(form).filter(k => JSON.stringify(form[k]) !== JSON.stringify(orig[k])), [form, orig]);
  const dirty = modificati.length > 0;

  // Avviso se si chiude la pagina con modifiche non salvate
  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const indietro = () => {
    if (dirty && !window.confirm('Ci sono modifiche non salvate. Uscire comunque?')) return;
    if (window.history.length > 1) navigate(-1);
    else navigate(`/ped${clienteId ? `?cliente_id=${clienteId}` : ''}`);
  };

  const onDate = (dp) => setForm(f => ({ ...f, data_pubblicazione: dp, orizzonte: orizzonteDaData(dp) }));

  const save = useCallback(async () => {
    if (!form.titolo_post.trim()) { error('Il titolo è obbligatorio'); return; }
    if (!clienteId) { error('Scegli il cliente prima di salvare'); return; }
    setSaving(true);
    try {
      if (isNew) {
        const payload = { cliente_id: clienteId };
        Object.keys(form).forEach(k => { payload[k] = valoreServer(k, form[k]); });
        const r = await api.post('/ped', payload);
        setOrig(form);
        success('Contenuto creato');
        navigate(`/ped/contenuto/${r.data.id}`, { replace: true });
      } else if (dirty) {
        // Solo i campi cambiati: non si sovrascrive il lavoro fatto nel frattempo da altri
        const patch = {};
        modificati.forEach(k => { patch[k] = valoreServer(k, form[k]); });
        const r = await api.patch(`/ped/${id}`, patch);
        const f = formDaRiga(r.data);
        setForm(f); setOrig(f); setMeta(r.data);
        success('Contenuto salvato');
      }
    } catch { /* toast globale */ }
    setSaving(false);
  }, [form, clienteId, isNew, dirty, modificati, id, navigate, success, error]);

  // Ctrl/⌘+S salva
  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') { e.preventDefault(); save(); } };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [save]);

  // Azioni rapide (spunte, incaricati): su un contenuto esistente si salvano subito
  const rapido = async (k, v) => {
    set(k, v);
    if (isNew) return;
    try {
      const r = await api.patch(`/ped/${id}`, { [k]: valoreServer(k, v) });
      setMeta(r.data);
      setOrig(o => ({ ...o, [k]: v }));
      if (r.data.stato && r.data.stato !== form.stato) {   // auto-avanzamento a "In Review"
        setForm(f => ({ ...f, stato: r.data.stato }));
        setOrig(o => ({ ...o, stato: r.data.stato }));
      }
    } catch { set(k, orig[k]); }
  };

  /* ── Media ─────────────────────────────────────────────────────────────── */
  const carica = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    for (const file of arr) {
      setUpload({ nome: file.name, pct: 0 });
      try {
        const fd = new FormData(); fd.append('file', file);
        const up = await api.post('/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (p) => { if (p.total) setUpload({ nome: file.name, pct: Math.round((p.loaded / p.total) * 100) }); },
        });
        const abs = up.data.url.startsWith('http') ? up.data.url : window.location.origin + up.data.url;
        setForm(f => ({ ...f, media_url: [...(f.media_url || []), abs] }));
      } catch { /* toast globale (es. file troppo grande) */ }
    }
    setUpload(null);
  };
  const removeMedia = (i) => setForm(f => ({ ...f, media_url: (f.media_url || []).filter((_, idx) => idx !== i) }));
  const spostaMedia = (i, dir) => setForm(f => {
    const m = [...(f.media_url || [])];
    const j = i + dir;
    if (j < 0 || j >= m.length) return f;
    [m[i], m[j]] = [m[j], m[i]];
    return { ...f, media_url: m };
  });

  // Media dal Client Vault, agganciati per codice (id_post)
  const [vaultMedia, setVaultMedia] = useState([]);
  useEffect(() => {
    const code = (form.id_post || '').trim();
    if (!clienteId || !code) { setVaultMedia([]); return; }
    api.get(`/vault/${clienteId}/handle`, { params: { code }, silent: true })
      .then(r => setVaultMedia(Array.isArray(r.data?.entries) ? r.data.entries : []))
      .catch(() => setVaultMedia([]));
  }, [clienteId, form.id_post]);
  const addVaultMedia = (en) => {
    const abs = window.location.origin + en.url;
    setForm(f => (f.media_url || []).includes(abs) ? f : { ...f, media_url: [...(f.media_url || []), abs] });
  };
  const isImgUrl = (n) => /\.(png|jpe?g|gif|webp|avif|bmp)$/i.test(n);

  /* ── Azioni ────────────────────────────────────────────────────────────── */
  const elimina = async () => {
    if (!window.confirm('Eliminare questo contenuto?')) return;
    await api.delete(`/ped/${id}`);
    setOrig(form);
    success('Contenuto eliminato');
    navigate(`/ped?cliente_id=${clienteId}`);
  };

  const duplica = async () => {
    if (dirty && !window.confirm('Ci sono modifiche non salvate: la copia partirà dall\'ultima versione salvata. Continuare?')) return;
    try {
      const r = await api.post(`/ped/${id}/duplica`, {});
      setOrig(form);
      success('Copia creata');
      navigate(`/ped/contenuto/${r.data.id}`);
    } catch { /* toast globale */ }
  };

  const ricarica = async () => { const r = await api.get(`/ped/riga/${id}`); setMeta(r.data); return r.data; };

  // Dopo azioni lato server che toccano la riga (fase, versione usata per la pubblicazione):
  // riallinea meta e i campi che il server può aver cambiato, così un "Salva" successivo
  // non riscrive valori vecchi (e non risultano modifiche non salvate fasulle).
  const refreshRiga = async () => {
    try {
      const d = await ricarica();
      const agg = { stato: d.stato || form.stato, media_url: Array.isArray(d.media_url) ? d.media_url : form.media_url };
      setForm(f => ({ ...f, ...agg }));
      setOrig(o => ({ ...o, ...agg }));
    } catch { /* toast globale */ }
  };

  const approva = async () => {
    try {
      await api.patch(`/ped/${id}/approva`, { approvato: true });
      await ricarica();
      set('stato', 'Approvato'); setOrig(o => ({ ...o, stato: 'Approvato' }));
      success('Contenuto approvato');
    } catch { /* toast globale */ }
  };

  const pubblica = async () => {
    if (dirty) { error('Salva le modifiche prima di programmare'); return; }
    setPublishing(true);
    try {
      const r = await api.post('/ped/publish', { ped_ids: [id] });
      const res = (r.data.results || [])[0] || {};
      if (res.ok) success('Programmato su PostProxy');
      else error(res.error || 'Pubblicazione non riuscita');
      await ricarica();
      loadStorico();
    } catch { /* toast globale */ }
    setPublishing(false);
  };

  const refreshStatus = async () => {
    setRefreshing(true);
    try {
      await api.post(`/ped/refresh_status?cliente_id=${clienteId}`);
      await ricarica();
      success('Stato allineato con PostProxy');
    } catch { /* toast globale */ }
    setRefreshing(false);
  };

  const resetPub = async () => {
    if (!window.confirm('Azzerare lo stato di pubblicazione? (non rimuove il post su PostProxy)')) return;
    await api.delete(`/ped/${id}/pubblicazione`);
    await ricarica();
    success('Stato pubblicazione azzerato');
  };

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-dim)' }}>Caricamento…</div>;

  const ss = STATO_STYLE[form.stato] || STATO_STYLE.Idea;
  const isStory = form.formato === 'Storia';
  const cliente = meta.cliente_azienda || meta.cliente_nome || clientiLista.find(c => c.id === clienteId)?.azienda || '';
  const dataPassata = form.data_pubblicazione && form.data_pubblicazione < new Date().toISOString().slice(0, 10) && meta?.pub_stato !== 'published';

  /* ── Sezioni ───────────────────────────────────────────────────────────── */
  const sezContenuto = (
    <Section title="Contenuto" key="contenuto" id="sez-contenuto">
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '130px 1fr', gap: 14, marginBottom: 14 }}>
        <Field label="Codice"><input className="eden-input" value={form.id_post} onChange={e => set('id_post', e.target.value)} placeholder="es. REEL003" style={{ fontFamily: 'ui-monospace, monospace' }} /></Field>
        <Field label="Titolo *"><input className="eden-input" value={form.titolo_post} onChange={e => set('titolo_post', e.target.value)} placeholder="Titolo del contenuto" /></Field>
      </div>
      <div style={{ marginBottom: 14 }}>
        <Field label="Hook" hint="La frase d'apertura che ferma lo scroll."><input className="eden-input" value={form.hook} onChange={e => set('hook', e.target.value)} placeholder="Hook, gancio, sottotitolo" /></Field>
      </div>
      <div className="form-grid-3" style={{ gap: 14, marginBottom: 14 }}>
        <Field label="Formato"><select className="eden-input" value={form.formato} onChange={e => set('formato', e.target.value)}>{FORMATI.map(f => <option key={f}>{f}</option>)}</select></Field>
        <Field label="Piattaforma"><select className="eden-input" value={form.piattaforma} onChange={e => set('piattaforma', e.target.value)}>{PIATTAFORME.map(p => <option key={p}>{p}</option>)}</select></Field>
        <Field label="Stato"><select className="eden-input" value={form.stato} onChange={e => set('stato', e.target.value)}>{STATI.map(s => <option key={s}>{s}</option>)}</select></Field>
      </div>
      <div className="form-grid-2" style={{ gap: 14 }}>
        <Field label="Data di pubblicazione"
          hint={dataPassata ? <span style={{ color: 'var(--red)' }}>Data passata: non verrà programmato su PostProxy</span> : form.data_pubblicazione ? ORIZZONTI_LABEL[form.orizzonte] : 'Senza data resta in bozza.'}>
          <input type="date" className="eden-input" value={form.data_pubblicazione || ''} onChange={e => onDate(e.target.value)} />
        </Field>
        <Field label="Orario" hint={form.ora_pubblicazione ? 'Orario esatto' : `Se vuoto: ${isStory ? 'storie alle 18:00' : 'post alle 12:00'}`}>
          <input type="time" className="eden-input" value={form.ora_pubblicazione || ''} onChange={e => set('ora_pubblicazione', e.target.value)} />
        </Field>
        {isStory && (
          <Field label="Modalità storia" full
            hint={form.modalita_storia === 'Auto' || !form.modalita_storia ? 'Esce da sola via PostProxy.' : 'Da postare a mano: compare come reminder in calendario.'}>
            <div className="eden-seg" style={{ width: '100%' }}>
              {[['Auto', 'Automatica'], ['Link', 'Link (a mano)'], ['Manuale', 'Manuale (a mano)']].map(([v, l]) => (
                <button type="button" key={v} className={(form.modalita_storia || 'Auto') === v ? 'is-on' : ''} style={{ flex: 1 }}
                  onClick={() => set('modalita_storia', v)}>{l}</button>
              ))}
            </div>
          </Field>
        )}
      </div>
    </Section>
  );

  const sezSquadra = (
    <Section title="Stato e approvazioni" icon={CheckCircle2} key="stato" id="sez-stato">
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {[['copy_ok', 'Copy pronto'], ['visual_ok', 'Visual pronto']].map(([k, label]) => (
          <button key={k} type="button" onClick={() => rapido(k, !form[k])} aria-pressed={form[k]}
            className="eden-btn" style={{ flex: 1, borderColor: form[k] ? 'transparent' : undefined, background: form[k] ? 'var(--green-dim)' : undefined, color: form[k] ? 'var(--green)' : 'var(--text-muted)' }}>
            {form[k] ? <Check size={15} strokeWidth={2.6} /> : <Clock size={15} />} {label}
          </button>
        ))}
      </div>
      <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {form.stato === 'Approvato' || meta.approvato_da ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>
            <CheckCircle2 size={16} /> Approvato{meta.approvato_da_nome ? ` da ${meta.approvato_da_nome}` : ''}
          </span>
        ) : (
          <>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Approvazione interna</span>
            {puoApprovare ? (
              <button type="button" onClick={approva} disabled={isNew} className="eden-btn is-sm is-success" style={{ marginLeft: 'auto' }}>Approva</button>
            ) : (
              <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--text-dim)' }}>
                <Clock size={14} /> In attesa del social media manager
              </span>
            )}
          </>
        )}
      </div>
      {!isNew && meta.cliente_appro && (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, padding: '10px 12px', borderRadius: 10,
          background: meta.cliente_appro === 'approvato' ? 'var(--green-dim)' : 'var(--yellow-dim)' }}>
          {meta.cliente_appro === 'approvato'
            ? <CheckCircle2 size={16} color="var(--green)" style={{ flexShrink: 0, marginTop: 1 }} />
            : <AlertCircle size={16} color="var(--yellow)" style={{ flexShrink: 0, marginTop: 1 }} />}
          <div style={{ fontSize: 13 }}>
            <b style={{ color: meta.cliente_appro === 'approvato' ? 'var(--green)' : 'var(--yellow)' }}>
              {meta.cliente_appro === 'approvato' ? 'Approvato dal cliente' : 'Il cliente chiede modifiche'}
            </b>
            {meta.cliente_feedback && <div style={{ color: 'var(--text-muted)', marginTop: 3 }}>«{meta.cliente_feedback}»</div>}
          </div>
        </div>
      )}
    </Section>
  );

  // Responsabile, videomaker, fase e scheda di produzione (componente della pipeline video)
  const sezProduzione = (
    <Section title="Team e produzione" icon={Users} key="produzione" id="sez-produzione">
      {isNew
        ? <div style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>Salva il contenuto per assegnare responsabile e videomaker e pianificare la produzione.</div>
        : <ProduzionePanel riga={meta} onSaved={refreshRiga} />}
    </Section>
  );

  const media = form.media_url || [];
  const sezMedia = (
    <Section title="File e media" icon={Film} key="media" id="sez-media"
      right={<span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{media.length ? `${media.length} ${media.length === 1 ? 'file' : 'file · carosello'}` : ''}</span>}>
      <div onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); carica(e.dataTransfer.files); }}
        style={{ borderRadius: 12, border: `2px dashed ${dragOver ? 'var(--cyan)' : 'var(--border-h)'}`, background: dragOver ? 'var(--cyan-dim)' : 'transparent', padding: media.length ? 12 : 22, transition: 'background 0.15s, border-color 0.15s' }}>
        {media.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 10, marginBottom: 12 }}>
            {media.map((u, i) => (
              <div key={`${u}-${i}`} style={{ position: 'relative', aspectRatio: '1 / 1', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)', background: 'var(--bg3)' }}>
                {isVideo(u) ? <video src={u} muted preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <img src={u} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                <span style={{ position: 'absolute', top: 5, left: 5, fontSize: 10.5, fontWeight: 800, padding: '1px 6px', borderRadius: 6, background: 'rgba(0,0,0,0.65)', color: '#fff' }}>{i + 1}</span>
                <button type="button" onClick={() => removeMedia(i)} aria-label="Rimuovi" style={thumbBtn({ top: 5, right: 5 })}><X size={12} /></button>
                {media.length > 1 && (
                  <div style={{ position: 'absolute', bottom: 5, left: 5, right: 5, display: 'flex', justifyContent: 'space-between' }}>
                    <button type="button" onClick={() => spostaMedia(i, -1)} disabled={i === 0} aria-label="Sposta prima" style={thumbBtn({ position: 'static', opacity: i === 0 ? 0.3 : 1 })}><ChevronLeft size={13} /></button>
                    <button type="button" onClick={() => spostaMedia(i, 1)} disabled={i === media.length - 1} aria-label="Sposta dopo" style={thumbBtn({ position: 'static', opacity: i === media.length - 1 ? 0.3 : 1 })}><ChevronRight size={13} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {upload ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 6 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Carico {upload.nome}</span><b>{upload.pct}%</b>
            </div>
            <div style={{ height: 6, borderRadius: 6, background: 'var(--bg3)', overflow: 'hidden' }}>
              <div style={{ width: `${upload.pct}%`, height: '100%', background: 'var(--cyan)', transition: 'width 0.2s' }} />
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap', textAlign: 'center' }}>
            <UploadCloud size={20} color="var(--text-dim)" />
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{isMobile ? 'Aggiungi foto o video' : 'Trascina qui foto e video, oppure'}</span>
            <button type="button" className="eden-btn is-sm" onClick={() => fileRef.current?.click()}><Plus size={14} />Scegli file</button>
            <span style={{ fontSize: 11.5, color: 'var(--text-dim)', width: '100%' }}>Immagini fino a 20MB · video fino a 500MB · l'ordine qui è l'ordine del carosello</span>
          </div>
        )}
        <input ref={fileRef} type="file" multiple accept="image/*,video/*" style={{ display: 'none' }} onChange={e => { carica(e.target.files); e.target.value = ''; }} />
      </div>

      {!isNew && <div style={{ marginTop: 12 }}><MediaCheck rid={id} deps={[meta.aggiornato_il]} /></div>}

      {/* Media dal Client Vault, agganciati per codice */}
      <div style={{ marginTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
            <HardDrive size={15} color="var(--text-dim)" /> Dal Client Vault
          </span>
          {clienteId && (
            <button type="button" onClick={() => navigate(`/client-vault?cliente=${clienteId}`)} className="eden-btn is-sm"><ExternalLink size={13} />Apri il Vault</button>
          )}
        </div>
        {!form.id_post ? (
          <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Imposta un Codice (es. REEL003) per agganciare automaticamente i file del Vault che iniziano con quel codice.</div>
        ) : vaultMedia.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Nessun file nel Vault che inizia con «{form.id_post}» (es. {form.id_post}_01.jpg).</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))', gap: 8 }}>
            {vaultMedia.map(en => {
              const abs = window.location.origin + en.url;
              const added = media.includes(abs);
              return (
                <button key={en.path} type="button" onClick={() => addVaultMedia(en)} disabled={added} title={en.name}
                  style={{ position: 'relative', aspectRatio: '1 / 1', borderRadius: 10, overflow: 'hidden', border: `1px solid ${added ? 'var(--green)' : 'var(--border)'}`, background: 'var(--bg3)', cursor: added ? 'default' : 'pointer', padding: 0, minHeight: 0 }}>
                  {isImgUrl(en.name)
                    ? <img src={en.url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><Film size={20} color="var(--purple)" /></span>}
                  <span style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: added ? 'var(--green)' : 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 10.5, fontWeight: 700, padding: '2px 0' }}>
                    {added ? 'Aggiunto' : '+ Aggiungi'}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Section>
  );

  const sezCaption = (
    <Section title="Caption" key="caption" id="sez-caption">
      <CaptionEditor clienteId={clienteId} value={form.caption_pubblica} onChange={v => set('caption_pubblica', v)}
        disabled={isStory} placeholder={isStory ? 'Le storie non hanno didascalia' : 'Testo del post, hashtag e menzioni…'} />
    </Section>
  );

  const sezAnteprima = (
    <Section title="Anteprima" icon={Eye} key="anteprima" right={<FormatTag formato={form.formato} />}>
      <InstagramPreview formato={form.formato} media={media} caption={isStory ? '' : form.caption_pubblica}
        cliente={cliente} titolo={form.titolo_post} />
    </Section>
  );

  // Versioni del montaggio + commenti di revisione (anche al timecode e dal portale cliente)
  const sezRevisione = !isNew && (
    <Section title="Montaggi e revisione" icon={Clapperboard} key="revisione">
      <VersioniReview rid={id} mediaUrl={media} onChanged={refreshRiga} />
    </Section>
  );

  const sezPubblicazione = !isNew && (
    <Section title="Pubblicazione su PostProxy" icon={Send} key="pubblicazione" id="sez-pubblicazione" right={meta.pub_stato && <PubPill stato={meta.pub_stato} />}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', fontSize: 13, color: 'var(--text-muted)' }}>
        {meta.pub_schedule_at && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Calendar size={14} /> {new Date(meta.pub_schedule_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>}
        {meta.pub_post_id && <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11.5 }}>#{meta.pub_post_id}</span>}
        {!meta.pub_stato && <span style={{ color: 'var(--text-dim)' }}>Non ancora programmato. Serve: approvazione, almeno un media e una data futura.</span>}
      </div>
      {meta.pub_stato === 'failed' && meta.pub_error && (
        <div style={{ fontSize: 12.5, color: 'var(--red)', marginTop: 10, background: 'var(--red-dim)', borderRadius: 9, padding: '8px 11px' }}>{meta.pub_error}</div>
      )}
      {meta.pub_insights && Object.keys(meta.pub_insights).length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)', display: 'flex', gap: 22, flexWrap: 'wrap' }}>
          {Object.entries(meta.pub_insights).filter(([k]) => k !== 'on').map(([k, v]) => (
            <div key={k}>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{Number(v).toLocaleString('it-IT')}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-dim)', textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</div>
            </div>
          ))}
        </div>
      )}
      {puoPubblicare ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          <button type="button" onClick={pubblica} disabled={publishing} className="eden-btn is-primary"><Send size={15} />{publishing ? 'Programmo…' : 'Programma su PostProxy'}</button>
          <button type="button" onClick={refreshStatus} disabled={refreshing} className="eden-btn"><RefreshCw size={14} className={refreshing ? 'eden-spin' : undefined} />{refreshing ? 'Aggiorno…' : 'Aggiorna stato'}</button>
          {meta.pub_stato && <button type="button" onClick={resetPub} className="eden-btn"><RotateCcw size={14} />Azzera stato</button>}
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: 'var(--text-dim)', marginTop: 12 }}>La programmazione la fa il social media manager.</div>
      )}
      {storico.length > 0 && (
        <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-dim)', marginBottom: 8 }}>Storico tentativi ({storico.length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {storico.map((s, i) => {
              const ok = s.esito === 'success';
              const p = s.payload || {};
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                  {ok ? <CheckCircle2 size={13} color="var(--green)" /> : <AlertCircle size={13} color="var(--red)" />}
                  <span style={{ color: 'var(--text-dim)' }}>{new Date(s.creato_il).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                  <span>{p.piattaforma || ''}{p.tipo ? ` · ${p.tipo}` : ''}</span>
                  {ok ? (p.post_id && <span style={{ fontFamily: 'ui-monospace, monospace' }}>#{p.post_id}</span>) : <span style={{ color: 'var(--red)' }}>{p.error || 'errore'}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Section>
  );

  const sezAltro = (
    <Collapsible title="Agenti AI, ads, budget e link" key="altro">
      <div className="form-grid-2" style={{ gap: 14 }}>
        <Field label="Agente copy (AI)"><input className="eden-input" value={form.agente_copy} onChange={e => set('agente_copy', e.target.value)} placeholder="Agente che scrive il copy" /></Field>
        <Field label="Agente visual (AI)"><input className="eden-input" value={form.agente_visual} onChange={e => set('agente_visual', e.target.value)} placeholder="Agente che produce il visual" /></Field>
        <Field label="Campagna ads"><input className="eden-input" value={form.ads_campagna} onChange={e => set('ads_campagna', e.target.value)} placeholder="Nome campagna" /></Field>
        <Field label="Stato ads"><input className="eden-input" value={form.ads_stato} onChange={e => set('ads_stato', e.target.value)} placeholder="es. attiva / in pausa" /></Field>
        <Field label="Speso (€)"><input type="number" className="eden-input" value={form.spending_speso} onChange={e => set('spending_speso', e.target.value)} placeholder="0" /></Field>
        <Field label="Budget (€)"><input type="number" className="eden-input" value={form.spending_budget} onChange={e => set('spending_budget', e.target.value)} placeholder="0" /></Field>
        <Field label="Link asset (drive/file)"><input className="eden-input" value={form.link_asset} onChange={e => set('link_asset', e.target.value)} placeholder="https://…" /></Field>
        <Field label="Link pubblicato"><input className="eden-input" value={form.link_pubblicato} onChange={e => set('link_pubblicato', e.target.value)} placeholder="https://… (permalink)" /></Field>
      </div>
    </Collapsible>
  );

  // "Prossimo passo": una sola indicazione su cosa fare adesso (solo su contenuti salvati)
  const { fatto, passo } = calcolaPasso({
    form, meta, hasMedia: media.some(Boolean), dataPassata,
    approvato: form.stato === 'Approvato' || form.stato === 'Pubblicato' || !!meta.approvato_da,
    puoPubblicare, puoApprovare,
  });
  const azionePasso = (a) => {
    if (a === 'copy_ok' || a === 'visual_ok') rapido(a, true);
    else if (a === 'approva') approva();
    else if (a === 'pubblica') pubblica();
  };
  const sezPasso = !isNew && (
    <ProssimoPasso key="passo" fatto={fatto} passo={passo} onAzione={azionePasso} busy={publishing} />
  );

  return (
    <div className="eden-page">
      {/* Intestazione */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0, flex: '1 1 320px' }}>
          <button type="button" onClick={indietro} aria-label="Indietro" className="eden-btn is-icon" style={{ flexShrink: 0 }}><ArrowLeft size={17} /></button>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{form.titolo_post || (isNew ? 'Nuovo contenuto' : 'Contenuto')}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap', fontSize: 13, color: 'var(--text-muted)' }}>
              {scegliCliente ? (
                <select className="eden-select" value={clienteId} onChange={e => setClienteId(e.target.value)} aria-label="Cliente"
                  style={{ height: 32, padding: '0 10px', fontSize: 13, borderColor: clienteId ? undefined : 'var(--yellow)' }}>
                  <option value="">Scegli il cliente…</option>
                  {clientiLista.map(c => <option key={c.id} value={c.id}>{c.azienda || c.nome}</option>)}
                </select>
              ) : cliente ? <span>{cliente}</span> : null}
              {form.id_post && <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, color: 'var(--text-dim)' }}>{form.id_post}</span>}
              <span style={{ fontSize: 11.5, padding: '3px 9px', borderRadius: 999, background: ss.bg, color: ss.color, fontWeight: 600 }}>{form.stato}</span>
              {meta.pub_stato && <PubPill stato={meta.pub_stato} />}
              {!isNew && <SegnaliContenuto r={meta} />}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {dirty && !isNew && <span style={{ fontSize: 12.5, color: 'var(--yellow)', fontWeight: 600 }}>Modifiche non salvate</span>}
          {!isNew && (
            <MoreMenu items={[
              { label: 'Duplica contenuto', icon: CopyIcon, onClick: duplica },
              { label: 'Elimina', icon: Trash2, onClick: elimina, danger: true, hidden: !puoPubblicare },
            ]} />
          )}
          <button type="button" onClick={save} disabled={saving || (!isNew && !dirty)} className="eden-btn is-primary" title="Salva (Ctrl+S)">
            <Save size={15} /> {saving ? 'Salvo…' : 'Salva'}
          </button>
        </div>
      </div>

      {isMobile ? (
        <>
          {sezPasso}{sezContenuto}{sezProduzione}{sezSquadra}{sezMedia}{sezRevisione}{sezCaption}{sezAnteprima}{sezPubblicazione}{sezAltro}
          {/* Barra di salvataggio sempre a portata di pollice quando ci sono modifiche */}
          {(dirty || isNew) && (
            <div style={{ position: 'sticky', bottom: 'calc(var(--tabbar-h) + env(safe-area-inset-bottom) + 10px)', zIndex: 20, display: 'flex', justifyContent: 'center' }}>
              <button type="button" onClick={save} disabled={saving} className="eden-btn is-primary" style={{ height: 46, padding: '0 22px', boxShadow: 'var(--shadow-lg)', borderRadius: 999 }}>
                <Save size={16} /> {saving ? 'Salvo…' : 'Salva modifiche'}
              </button>
            </div>
          )}
        </>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: 18, alignItems: 'start' }}>
          <div style={{ minWidth: 0 }}>{sezContenuto}{sezProduzione}{sezMedia}{sezRevisione}{sezCaption}{sezPubblicazione}{sezAltro}</div>
          <div style={{ position: 'sticky', top: 'calc(var(--topbar-h) + 16px)', maxHeight: 'calc(100dvh - var(--topbar-h) - 32px)', overflowY: 'auto', paddingRight: 2 }}>
            {sezPasso}{sezSquadra}{sezAnteprima}
          </div>
        </div>
      )}
    </div>
  );
}

function thumbBtn(extra) {
  return {
    position: 'absolute', width: 22, height: 22, minHeight: 0, borderRadius: '50%', border: 'none', cursor: 'pointer',
    background: 'rgba(0,0,0,0.7)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
    ...extra,
  };
}
